import { useRef, useState } from 'react';
import { Camera } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { cn } from '@/shared/lib/cn';

interface Props {
  title: string;
  description: string;
  accept?: string;
  previewUrl?: string | null;
  aspect?: 'banner' | 'square' | 'logo';
  onSelect: (file: File) => void | Promise<void>;
  busy?: boolean;
}

export function ImageUploadCard({
  title,
  description,
  accept = 'image/png, image/jpeg',
  previewUrl,
  aspect = 'banner',
  onSelect,
  busy,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const src = localPreview ?? previewUrl ?? undefined;

  const heightClass =
    aspect === 'banner' ? 'h-40' : aspect === 'logo' ? 'h-32' : 'h-36 w-36 mx-auto';

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="text-base">{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
        <Button size="sm" variant="secondary" loading={busy} onClick={() => inputRef.current?.click()}>
          <Camera className="size-4" /> Alterar
        </Button>
      </CardHeader>
      <CardContent>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className={cn(
            'group relative grid w-full place-items-center overflow-hidden rounded-xl border border-dashed border-border bg-muted/30 transition-[border-color,box-shadow] duration-300 ease-out',
            'hover:border-primary/45 hover:shadow-[inset_0_0_0_1px_hsl(var(--primary)/0.12)]',
            'dark:hover:border-primary/35',
            heightClass,
          )}
          style={
            src
              ? { backgroundImage: `url(${src})`, backgroundSize: 'cover', backgroundPosition: 'center' }
              : undefined
          }
        >

          {/* Fade suave no hover — dourado no light, escuro no dark */}
          <div
            aria-hidden
            className={cn(
              'pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 ease-out group-hover:opacity-100',
              'bg-primary/[0.08] dark:bg-black/45',
            )}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 transition-all duration-300 ease-out group-hover:opacity-100"
          >
            <Camera
              className={cn(
                'size-8 translate-y-0.5 scale-95 transition-transform duration-300 ease-out group-hover:translate-y-0 group-hover:scale-100',
                'text-primary dark:text-white',
              )}
            />
          </div>
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          hidden
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            setLocalPreview(URL.createObjectURL(file));
            await onSelect(file);
            e.target.value = '';
          }}
        />
        <p className="mt-2 text-xs text-muted-foreground">PNG ou JPEG, até 8 MB.</p>
      </CardContent>
    </Card>
  );
}
